import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Tag as TagIcon,
  Timer as TimerIcon,
  Update as UpdateIcon,
  AccessTime as AccessTimeIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import CustomButton from '../../../components/common/Button';
import { Driver, ShiftStatus, Shift } from '../types';
import { formatDateTime } from '../utils';
import DatePickerDDMMYY from '../../../components/common/DatePickerDDMMYY';
import { cityOptions } from '../../../constants/enums';
import { ASSIGN_COURIER_TO_TEMPLATE, UNASSIGN_COURIER_FROM_TEMPLATE, GENERATE_SHIFTS_FROM_TEMPLATES, FETCH_SHIFT_TEMPLATE, UPDATE_SHIFT_TEMPLATE } from '../../../api/mutations/ShiftManagementMutations';
import { stagingOptions } from '../../../constants/apollo';
import { ShiftTemplate as ShiftTemplateType, RecurrenceType, TemplateStatus } from '../types';

const ShiftTemplateDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: templateData, loading: templateLoading, error: templateError, refetch: refetchTemplate } = useQuery(FETCH_SHIFT_TEMPLATE, {
    ...stagingOptions,
    variables: { id: id || '' },
    skip: !id,
    fetchPolicy: 'network-only',
  });

  const template: ShiftTemplateType | undefined = useMemo(() => {
    if (!templateData?.fetchShiftTemplate) return undefined;

    const apiTemplate = templateData.fetchShiftTemplate;
    return {
      id: apiTemplate.id,
      name: apiTemplate.name,
      startTime: apiTemplate.startTime,
      endTime: apiTemplate.endTime,
      requiredDrivers: apiTemplate.requiredDrivers || 1,
      cityId: apiTemplate.city?.id || undefined,
      neighborhoodId: apiTemplate.neighborhood?.id || undefined,
      recurrenceType: (apiTemplate.recurrenceType?.toLowerCase() || 'daily') as RecurrenceType,
      daysOfWeek: apiTemplate.daysOfWeek || [],
      daysOfWeekNames: [],
      status: (apiTemplate.status?.toLowerCase() || 'active') as TemplateStatus,
      courierIds: apiTemplate.couriers?.map((c: any) => c.id) || [],
      validFrom: apiTemplate.validFrom || undefined,
      validUntil: apiTemplate.validUntil || undefined,
      excludeDates: apiTemplate.excludeDates || [],
      priority: apiTemplate.priority || undefined,
      specificDate: apiTemplate.specificDate || undefined,
      generateDaysAhead: apiTemplate.generateDaysAhead || 7,
      zone: apiTemplate.neighborhood?.name || undefined,
      createdAt: apiTemplate.createdAt,
      updatedAt: apiTemplate.updatedAt,
    };
  }, [templateData]);

  const assignedCouriersFromAPI = useMemo(() => {
    if (!templateData?.fetchShiftTemplate?.couriers) return [];
    return templateData.fetchShiftTemplate.couriers.map((courier: any) => ({
      id: courier.id,
      firstName: courier.firstName || '',
      lastName: courier.lastName || '',
      name: courier.firstName && courier.lastName
        ? `${courier.firstName} ${courier.lastName}`
        : courier.firstName || courier.lastName || 'Unknown',
      mobileNumber: '',
      phone: '',
      status: 'Idle' as Driver['status'],
      latitude: undefined,
      longitude: undefined,
      avatarUrl: undefined,
      rating: undefined,
      vehicleType: undefined,
      isBanned: false,
      lastSeen: undefined,
    }));
  }, [templateData]);

  const shiftsFromAPI: Shift[] = useMemo(() => {
    if (!templateData?.fetchShiftTemplate?.shifts) return [];
    return templateData.fetchShiftTemplate.shifts.map((shift: any): Shift => ({
      id: shift.id,
      name: shift.name || `Shift ${shift.id}`,
      startTime: shift.startTime,
      endTime: shift.endTime || shift.startTime,
      requiredDrivers: template?.requiredDrivers || 1,
      maxDrivers: template?.requiredDrivers || 1,
      status: (shift.status?.toLowerCase() || 'draft') as ShiftStatus,
      cityId: template?.cityId || '',
      neighborhoodId: template?.neighborhoodId,
      zone: template?.zone,
      assignedDriversCount: 0,
      availableSlots: template?.requiredDrivers || 1,
      startLatitude: undefined,
      startLongitude: undefined,
      createdAt: undefined,
      updatedAt: undefined,
    }));
  }, [templateData, template]);

  const [selectedCourierId, setSelectedCourierId] = useState('');
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateStartDate, setGenerateStartDate] = useState('');
  const [generateEndDate, setGenerateEndDate] = useState('');

  const [assignCourierToTemplateMutation] = useMutation(ASSIGN_COURIER_TO_TEMPLATE, {
    ...stagingOptions,
  });
  const [unassignCourierFromTemplateMutation] = useMutation(UNASSIGN_COURIER_FROM_TEMPLATE, {
    ...stagingOptions,
  });
  const [generateShiftsFromTemplatesMutation] = useMutation(GENERATE_SHIFTS_FROM_TEMPLATES, {
    ...stagingOptions,
    fetchPolicy: 'no-cache',
  });
  const [updateShiftTemplateMutation] = useMutation(UPDATE_SHIFT_TEMPLATE, {
    ...stagingOptions,
  });

  if (templateLoading) {
    return (
      <div className="main-wrapper">
        <div className="w-full px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primaryColor mx-auto mb-4"></div>
              <p className="text-gray-600">Loading template...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (templateError) {
    return (
      <div className="main-wrapper">
        <div className="w-full px-6 lg:px-8 py-8">
          <p className="text-gray-600">Error loading template: {templateError.message}</p>
          <button
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/shift-management');
              }
            }}
            className="mt-4 text-primaryColor hover:text-darkBlue"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  if (!templateLoading && !template) {
    return (
      <div className="main-wrapper">
        <div className="w-full px-6 lg:px-8 py-8">
          <p className="text-gray-600">Template not found</p>
          <button
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/shift-management');
              }
            }}
            className="mt-4 text-primaryColor hover:text-darkBlue"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  if (!template) {
    return null;
  }

  const getCityName = (cityId?: string | number) => {
    if (!cityId) return 'All Cities';
    const city = cityOptions.find(c => String(c.id) === String(cityId));
    return city?.name || String(cityId);
  };

  const getNeighborhoodName = (neighborhoodId?: string) => {
    if (!neighborhoodId) return 'N/A';
    return neighborhoodId;
  };

  const getRecurrenceLabel = (): string => {
    switch (template.recurrenceType) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return template.daysOfWeekNames?.join(', ') || 'Weekly';
      case 'specific_date':
        return `Date: ${template.specificDate || 'N/A'}`;
      case 'custom_range':
        return `${template.validFrom || 'N/A'} to ${template.validUntil || 'N/A'}`;
      default:
        return template.recurrenceType;
    }
  };

  const getRecurrenceColor = (): string => {
    switch (template.recurrenceType) {
      case 'specific_date':
        return 'bg-purple-100 text-purple-800';
      case 'weekly':
        return 'bg-blue-100 text-blue-800';
      case 'daily':
        return 'bg-indigo-100 text-indigo-800';
      case 'custom_range':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (): string => {
    return template.status === 'active'
      ? 'from-green-500 via-green-600 to-green-700'
      : 'from-gray-500 via-gray-600 to-gray-700';
  };

  const assignedCouriers = assignedCouriersFromAPI;

  const generatedShifts: Shift[] = shiftsFromAPI;

  const activeShiftsCount = generatedShifts.filter((s: Shift) => s.status === 'active').length;
  const completedShiftsCount = generatedShifts.filter((s: Shift) => s.status === 'completed').length;
  const draftShiftsCount = generatedShifts.filter((s: Shift) => s.status === 'draft').length;
  const cancelledShiftsCount = generatedShifts.filter((s: Shift) => s.status === 'cancelled').length;
  const completionRate = generatedShifts.length > 0
    ? Math.round((completedShiftsCount / generatedShifts.length) * 100)
    : 0;


  const handleAddCourier = async () => {
    if (!selectedCourierId || !id) {
      toast.error('Please enter a courier ID');
      return;
    }
    try {
      const response = await assignCourierToTemplateMutation({
        variables: {
          input: {
            shiftTemplateId: id,
            courierId: selectedCourierId,
          },
        },
      });

      if (response.data?.assignCourierToTemplate?.shiftTemplate) {
        toast.success('Courier assigned to template successfully');
        setSelectedCourierId('');
        window.location.reload();
      }
    } catch (error: any) {
      console.error('Failed to assign courier', error);
      toast.error(error.message || 'Failed to assign courier. Please try again.');
    }
  };

  const handleRemoveCourier = async (courierId: string) => {
    if (!id) return;

    if (window.confirm('Are you sure you want to remove this courier from the template?')) {
      try {
        const response = await unassignCourierFromTemplateMutation({
          variables: {
            input: {
              shiftTemplateId: id,
              courierId: courierId,
            },
          },
        });

        if (response.data?.unassignCourierFromTemplate?.success) {
          toast.success('Courier removed from template successfully');
          await refetchTemplate();
        }
      } catch (error: any) {
        console.error('Failed to remove courier', error);
        toast.error(error.message || 'Failed to remove courier. Please try again.');
      }
    }
  };

  const handleToggleStatus = async () => {
    if (!id) {
      toast.error('Template ID is missing');
      return;
    }

    const newStatus = template.status === 'active' ? 'INACTIVE' : 'ACTIVE';

    try {
      const response = await updateShiftTemplateMutation({
        variables: {
          input: {
            id: id,
            status: newStatus,
          },
        },
      });

      if (response.data?.updateShiftTemplate?.shiftTemplate) {
        toast.success(`Template ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`);
        await refetchTemplate();
      } else {
        toast.error('Failed to update template status');
      }
    } catch (error: any) {
      console.error('Failed to toggle template status', error);
      const errorMessage = error.graphQLErrors?.[0]?.message || error.message || 'Failed to update template status. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleGenerateShifts = async () => {
    if (!generateStartDate || !generateEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (!id) {
      toast.error('Template ID is missing');
      return;
    }

    const cityId = template?.cityId;
    if (!cityId) {
      toast.error('Template must have a city assigned to generate shifts');
      return;
    }

    try {
      const cityIdValue = typeof cityId === 'string' ? parseInt(cityId, 10) : cityId;

      const response = await generateShiftsFromTemplatesMutation({
        variables: {
          input: {
            startDate: generateStartDate,
            endDate: generateEndDate,
            cityId: cityIdValue,
          },
        },
      });

      const responseWithError = response as any;
      if (responseWithError.error) {
        const errorMessage = responseWithError.error.message || 'Failed to generate shifts';
        console.error('Response error:', responseWithError.error);
        toast.error(errorMessage);
        return;
      }

      if (response.errors && response.errors.length > 0) {
        const errorMessage = response.errors[0]?.message || 'Failed to generate shifts';
        console.error('GraphQL errors:', response.errors);
        toast.error(errorMessage);
        return;
      }

      if (response.data?.generateShiftsFromTemplates) {
        const result = response.data.generateShiftsFromTemplates;
        const createdCount = result.createdCount || 0;
        const skippedCount = result.skippedCount || 0;
        const errors = result.errors || [];

        if (errors && errors.length > 0) {
          toast.warning(`Generated ${createdCount} shifts, skipped ${skippedCount}, with ${errors.length} error(s)`);
        } else {
          toast.success(`Successfully generated ${createdCount} shift(s), skipped ${skippedCount}`);
        }
        setShowGenerateDialog(false);
        setGenerateStartDate('');
        setGenerateEndDate('');
        await refetchTemplate();
      } else if (response.data && Object.keys(response.data).length === 0) {
        console.error('Empty response data, likely a backend error');
        toast.error('Failed to generate shifts. Backend service error. Please contact support.');
      } else {
        console.error('Unexpected response structure:', response);
        console.error('Response data:', response.data);
        toast.error('Failed to generate shifts. Unexpected response structure.');
      }
    } catch (error: any) {
      console.error('Failed to generate shifts', error);
      console.error('Error details:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
      });
      const errorMessage = error.graphQLErrors?.[0]?.message || error.networkError?.message || error.message || 'Failed to generate shifts. Please try again.';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="flex flex-col space-y-2 min-h-screen bg-gray-50">
      <div className="main-wrapper">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">

          <button
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/shift-management');
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors mb-4 group"
          >
            <ArrowBackIcon sx={{ fontSize: '20px' }} className="group-hover:translate-x-[-2px] transition-transform" />
            <span className="text-sm font-medium">Back</span>
          </button>

          <div className="mb-4">
            <div className={`bg-gradient-to-r ${getStatusColor()} rounded-xl p-4 lg:p-6 shadow-lg relative overflow-hidden`}>
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24"></div>
              <div className="absolute bottom-0 left-0 w-36 h-36 bg-white/5 rounded-full -ml-18 -mb-18"></div>
              <div className="relative z-10">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-lg border border-white/30">
                      <ScheduleIcon sx={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h1 className="text-2xl lg:text-3xl font-bold text-white drop-shadow-lg">{template.name}</h1>
                        <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold text-white border border-white/30">
                          {template.status.toUpperCase()}
                        </span>
                        <Chip
                          label={getRecurrenceLabel()}
                          size="small"
                          className={getRecurrenceColor()}
                          sx={{ fontSize: '0.7rem', height: '22px', fontWeight: 600 }}
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-white/95">
                        <div className="flex items-center gap-1.5">
                          <ScheduleIcon sx={{ fontSize: '16px' }} />
                          <span className="font-medium">{template.startTime} - {template.endTime}</span>
                        </div>
                        {template.zone && (
                          <div className="flex items-center gap-1.5">
                            <LocationIcon sx={{ fontSize: '16px' }} />
                            <span className="font-medium">{template.zone}</span>
                          </div>
                        )}
                        {template.cityId && !template.zone && (
                          <div className="flex items-center gap-1.5">
                            <LocationIcon sx={{ fontSize: '16px' }} />
                            <span className="font-medium">{getCityName(template.cityId)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Tooltip title="Edit Template" arrow>
                      <IconButton
                        onClick={() => navigate(`/shift-management/templates/${id}/edit`)}
                        className="bg-white/20 hover:bg-white/30 border border-white/30"
                        sx={{ color: 'white' }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={template.status === 'active' ? 'Deactivate Template' : 'Activate Template'} arrow>
                      <IconButton
                        onClick={handleToggleStatus}
                        className="bg-white/20 hover:bg-white/30 border border-white/30"
                        sx={{ color: 'white' }}
                      >
                        {template.status === 'active' ? <StopIcon /> : <PlayIcon />}
                      </IconButton>
                    </Tooltip>
                    {/* Temporarily hidden: Generate Shifts */}
                    {/* <Tooltip title="Generate Shifts" arrow>
                      <IconButton
                        onClick={() => setShowGenerateDialog(true)}
                        disabled={template.status === 'inactive'}
                        className="bg-white/20 hover:bg-white/30 border border-white/30 disabled:opacity-50"
                        sx={{ color: 'white' }}
                      >
                        <PlayIcon />
                      </IconButton>
                    </Tooltip> */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap mb-6">
            <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shadow-sm">
                <PeopleIcon sx={{ fontSize: '20px', color: '#3B82F6' }} />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Pre-assigned Couriers</p>
                <p className="text-xl font-bold text-gray-900">{assignedCouriers.length} / {template.requiredDrivers}</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-purple-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center shadow-sm">
                <ScheduleIcon sx={{ fontSize: '20px', color: '#9333EA' }} />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Generated Shifts Sessions</p>
                <p className="text-xl font-bold text-gray-900">{generatedShifts.length}</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-green-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center shadow-sm">
                <CheckCircleIcon sx={{ fontSize: '20px', color: '#10B981' }} />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Active Shifts</p>
                <p className="text-xl font-bold text-gray-900">{activeShiftsCount}</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-amber-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br flex items-center justify-center shadow-sm ${template.status === 'active'
                  ? 'from-green-100 to-green-50'
                  : 'from-gray-100 to-gray-50'
                }`}>
                <CheckCircleIcon sx={{
                  fontSize: '20px',
                  color: template.status === 'active' ? '#10B981' : '#6B7280'
                }} />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Template Status</p>
                <p className="text-xl font-bold text-gray-900">{template.status === 'active' ? 'ON' : 'OFF'}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2 space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primaryColor to-darkBlue flex items-center justify-center">
                    <ScheduleIcon sx={{ fontSize: '18px', color: 'white' }} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Shift Scheduler Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <TagIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">ID</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900 font-mono">#{template.id}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <StarIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Priority</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{template.priority || 'Auto'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <PeopleIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Required Drivers</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{template.requiredDrivers}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <CalendarIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Generate Days Ahead</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{template.generateDaysAhead || 7}</p>
                  </div>
                  {templateData?.fetchShiftTemplate?.city && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">City</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{templateData.fetchShiftTemplate.city.name}</p>
                    </div>
                  )}
                  {!templateData?.fetchShiftTemplate?.city && template.cityId && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">City</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{getCityName(template.cityId)}</p>
                    </div>
                  )}
                  {templateData?.fetchShiftTemplate?.neighborhood && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Neighborhood</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{templateData.fetchShiftTemplate.neighborhood.name}</p>
                    </div>
                  )}
                  {!templateData?.fetchShiftTemplate?.neighborhood && (template.neighborhoodId || template.zone) && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Neighborhood</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{template.zone || getNeighborhoodName(template.neighborhoodId)}</p>
                    </div>
                  )}
                  {template.startLatitude && template.startLongitude && (
                    <>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-1.5 mb-1">
                          <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Latitude</span>
                        </div>
                        <p className="text-sm font-bold text-gray-900">{template.startLatitude.toFixed(6)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-1.5 mb-1">
                          <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Longitude</span>
                        </div>
                        <p className="text-sm font-bold text-gray-900">{template.startLongitude.toFixed(6)}</p>
                      </div>
                    </>
                  )}
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <CalendarIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Recurrence Type</span>
                    </div>
                    <Chip
                      label={template.recurrenceType}
                      size="small"
                      className={getRecurrenceColor()}
                      sx={{ fontSize: '0.7rem', height: '20px', fontWeight: 600 }}
                    />
                  </div>
                  {template.validFrom && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <CalendarIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Valid From</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{template.validFrom}</p>
                    </div>
                  )}
                  {template.validUntil && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <CalendarIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Valid Until</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{template.validUntil}</p>
                    </div>
                  )}
                  {template.createdAt && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <CalendarIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Created At</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{formatDateTime(template.createdAt)}</p>
                    </div>
                  )}
                  {template.updatedAt && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <UpdateIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Updated At</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{formatDateTime(template.updatedAt)}</p>
                    </div>
                  )}
                  {template.excludeDates && template.excludeDates.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <CloseIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Excluded Dates</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {template.excludeDates.map((date) => (
                          <Chip
                            key={date}
                            label={new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            size="small"
                            className="bg-red-50 text-red-800 border border-red-200"
                            sx={{ fontSize: '0.65rem', height: '20px' }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                      <PeopleIcon sx={{ fontSize: '20px', color: 'white' }} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Pre-assigned Couriers</h3>
                    <span className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">
                      {assignedCouriers.length} / {template.requiredDrivers}
                    </span>
                  </div>
                </div>

                {assignedCouriers.length < template.requiredDrivers && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <AddIcon sx={{ fontSize: '18px', color: '#3B82F6' }} />
                      <h4 className="text-sm font-semibold text-gray-900">Add Courier</h4>
                    </div>
                    <div className="flex items-end gap-2">
                      <TextField
                        className="flex-1"
                        label="Courier ID"
                        placeholder="Enter courier ID"
                        value={selectedCourierId}
                        onChange={(e) => setSelectedCourierId(e.target.value)}
                        size="small"
                        fullWidth
                      />
                      <CustomButton
                        variant="contained"
                        color="primary"
                        onClick={handleAddCourier}
                        disabled={!selectedCourierId}
                        additionalStyles="text-sm py-2.5 px-4 shadow-md hover:shadow-lg transition-all"
                      >
                        <AddIcon sx={{ fontSize: '16px', marginRight: '4px' }} />
                        Add
                      </CustomButton>
                    </div>
                  </div>
                )}

                {assignedCouriers.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <PeopleIcon sx={{ fontSize: '56px', color: '#9CA3AF', marginBottom: '12px' }} />
                    <p className="text-sm font-semibold text-gray-600 mb-1">No couriers pre-assigned</p>
                    <p className="text-xs text-gray-500">Add couriers to automatically assign them when shifts are generated</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b-2 border-gray-300">
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Courier</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Contact</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {assignedCouriers.map((driver: Driver, index: number) => {
                          const bgColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50';
                          return (
                            <tr
                              key={driver.id}
                              className={`${bgColor} hover:bg-gray-100 transition-colors duration-150`}
                            >
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primaryColor to-darkBlue flex items-center justify-center text-white font-bold text-sm shadow-md">
                                    {driver.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <div className="text-sm font-bold text-gray-900">{driver.name}</div>
                                    <div className="text-xs text-gray-500 font-mono">ID: {driver.id.slice(0, 8)}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-1.5 text-sm text-gray-700">
                                  <PhoneIcon sx={{ fontSize: '14px', color: '#6B7280' }} />
                                  <span className="font-medium">{driver.phone}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold border ${driver.status === 'Online'
                                      ? 'bg-green-100 text-green-800 border-green-300'
                                      : driver.status === 'Busy'
                                        ? 'bg-amber-100 text-amber-800 border-amber-300'
                                        : 'bg-gray-100 text-gray-800 border-gray-300'
                                    }`}
                                >
                                  {driver.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <Tooltip title="Remove from template" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveCourier(driver.id)}
                                    className="hover:bg-red-50"
                                    sx={{
                                      padding: '6px',
                                      '&:hover': {
                                        backgroundColor: '#FEE2E2',
                                      }
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: '18px', color: '#EF4444' }} />
                                  </IconButton>
                                </Tooltip>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-md">
                    <ScheduleIcon sx={{ fontSize: '20px', color: 'white' }} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Generated Shifts Sessions</h3>
                  <span className="px-2.5 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-bold">
                    {generatedShifts.length}
                  </span>
                </div>
                {generatedShifts.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <ScheduleIcon sx={{ fontSize: '56px', color: '#9CA3AF', marginBottom: '12px' }} />
                    <p className="text-sm font-semibold text-gray-600 mb-1">No shifts generated yet</p>
                    {/* Temporarily hidden: Generate Shifts button reference */}
                    {/* <p className="text-xs text-gray-500">Use the "Generate Shifts" button to create shifts from this template</p> */}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b-2 border-gray-300">
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Shift Name</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Start Time</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">End Time</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {generatedShifts.map((shift: Shift, index: number) => {
                          const bgColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50';
                          return (
                            <tr key={shift.id} className={`${bgColor} hover:bg-gray-100 transition-colors duration-150`}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex flex-col">
                                  <span className="text-sm font-semibold text-gray-900">{shift.name}</span>
                                  <span className="text-xs font-mono text-gray-500 mt-0.5">#{shift.id}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-700">{formatDateTime(shift.startTime)}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-700">{formatDateTime(shift.endTime)}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold border ${shift.status === 'active'
                                      ? 'bg-green-100 text-green-800 border-green-300'
                                      : shift.status === 'completed'
                                        ? 'bg-blue-100 text-blue-800 border-blue-300'
                                        : shift.status === 'draft'
                                          ? 'bg-gray-100 text-gray-800 border-gray-300'
                                          : 'bg-red-100 text-red-800 border-red-300'
                                    }`}
                                >
                                  {shift.status.toUpperCase()}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <Tooltip title="View Shift Details" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/shift-management/shifts/${shift.id}`)}
                                    className="hover:bg-primaryColor/10"
                                  >
                                    <VisibilityIcon sx={{ fontSize: '18px', color: '#6B7280' }} />
                                  </IconButton>
                                </Tooltip>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="xl:col-span-1 space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <ScheduleIcon sx={{ fontSize: '18px', color: 'white' }} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Shift Status</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-xs font-semibold text-gray-700">Active</span>
                      </div>
                      <span className="text-base font-bold text-gray-900">{activeShiftsCount}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500"
                        style={{
                          width: `${generatedShifts.length > 0 ? (activeShiftsCount / generatedShifts.length) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-xs font-semibold text-gray-700">Completed</span>
                      </div>
                      <span className="text-base font-bold text-gray-900">{completedShiftsCount}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                        style={{
                          width: `${generatedShifts.length > 0 ? (completedShiftsCount / generatedShifts.length) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                        <span className="text-xs font-semibold text-gray-700">Draft</span>
                      </div>
                      <span className="text-base font-bold text-gray-900">{draftShiftsCount}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-gray-500 to-gray-600 rounded-full transition-all duration-500"
                        style={{
                          width: `${generatedShifts.length > 0 ? (draftShiftsCount / generatedShifts.length) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  {cancelledShiftsCount > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <span className="text-xs font-semibold text-gray-700">Cancelled</span>
                        </div>
                        <span className="text-base font-bold text-gray-900">{cancelledShiftsCount}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all duration-500"
                          style={{
                            width: `${generatedShifts.length > 0 ? (cancelledShiftsCount / generatedShifts.length) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {generatedShifts.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                      <CheckCircleIcon sx={{ fontSize: '18px', color: 'white' }} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Completion</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-gray-700">Completed</span>
                        <span className="text-base font-bold text-green-600">
                          {completedShiftsCount} ({completionRate}%)
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500"
                          style={{ width: `${completionRate}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-gray-700">In Progress</span>
                        <span className="text-base font-bold text-blue-600">
                          {activeShiftsCount} ({generatedShifts.length > 0 ? Math.round((activeShiftsCount / generatedShifts.length) * 100) : 0}%)
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                          style={{ width: `${generatedShifts.length > 0 ? (activeShiftsCount / generatedShifts.length) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                    <div className="pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-gray-700">Total Shifts</span>
                        <span className="text-lg font-bold text-gray-900">
                          {generatedShifts.length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 shadow-md p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center">
                    <TrendingUpIcon sx={{ fontSize: '18px', color: 'white' }} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Quick Stats</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-200">
                    <span className="text-xs font-medium text-gray-600">Time Range</span>
                    <span className="text-sm font-bold text-gray-900">{template.startTime} - {template.endTime}</span>
                  </div>
                  <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-200">
                    <span className="text-xs font-medium text-gray-600">Generate Days Ahead</span>
                    <span className="text-sm font-bold text-primaryColor">{template.generateDaysAhead || 7}</span>
                  </div>
                  <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-200">
                    <span className="text-xs font-medium text-gray-600">Pre-assigned Couriers</span>
                    <span className="text-sm font-bold text-gray-900">{assignedCouriers.length}/{template.requiredDrivers}</span>
                  </div>
                  <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-200">
                    <span className="text-xs font-medium text-gray-600">Completion Rate</span>
                    <span className={`text-sm font-bold ${completionRate >= 80 ? 'text-green-600' : 'text-red-600'}`}>
                      {completionRate}%
                    </span>
                  </div>
                  {template.priority && (
                    <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-200">
                      <span className="text-xs font-medium text-gray-600">Priority</span>
                      <span className="text-sm font-bold text-gray-900">{template.priority}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-200">
                    <span className="text-xs font-medium text-gray-600">Recurrence</span>
                    <Chip
                      label={getRecurrenceLabel()}
                      size="small"
                      className={getRecurrenceColor()}
                      sx={{ fontSize: '0.65rem', height: '20px', fontWeight: 600 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Dialog
            open={showGenerateDialog}
            onClose={() => setShowGenerateDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon sx={{ fontSize: '20px', color: '#6B7280' }} />
              <span>Generate Shifts from Template</span>
            </DialogTitle>
            <DialogContent>
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <DatePickerDDMMYY
                    value={generateStartDate}
                    onChange={(value) => setGenerateStartDate(value)}
                    placeholder="Start Date: dd/mm/yy"
                    tooltip="Select the start date for generating shifts. Click to open calendar picker."
                    fullWidth
                    size="small"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <DatePickerDDMMYY
                    value={generateEndDate}
                    onChange={(value) => setGenerateEndDate(value)}
                    placeholder="End Date: dd/mm/yy"
                    tooltip="Select the end date for generating shifts. Click to open calendar picker. Must be after start date."
                    minDate={generateStartDate}
                    fullWidth
                    size="small"
                  />
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Shifts will be generated for each date in the range that matches this template's recurrence pattern.
                    Excluded dates will be skipped.
                  </p>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <CustomButton
                variant="outlined"
                onClick={() => {
                  setShowGenerateDialog(false);
                  setGenerateStartDate('');
                  setGenerateEndDate('');
                }}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="contained"
                onClick={handleGenerateShifts}
                disabled={!generateStartDate || !generateEndDate}
              >
                Generate Shifts
              </CustomButton>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default ShiftTemplateDetails;

